/**
 * Last season, read once.
 *
 * THE TRAP THIS MODULE EXISTS TO CLOSE. `season-2025.json` now holds two orderings of the same
 * ten men, and Samant is 7th by one and 8th by the other. Six screens used to each do
 * `standings.map(s => s.gm)` and call the result "the finish order", which was fine while there
 * was only one ordering and is a coin-flip now. So the file is read here and nowhere else, and
 * every export says in its name which fact it is.
 *
 *   FINAL_ORDER    the order of finish. 1-6 from the playoff bracket, 7-10 re-ordered by the
 *                  consolation bracket. This is what "worst finisher first" means, what the
 *                  chalk order reverses, and what identifies the Toilet Bowl winner.
 *   REGULAR_ORDER  where everybody actually came in the regular season, from Yahoo's own bracket
 *                  seeds. Nothing else in the league uses it.
 *   LOTTERY_BASIS  the two spliced together at `playoffTeams`, which is the only thing the odds
 *                  are ever keyed to (Pass 06 §1).
 *
 * All three are GM ids, index 0 = first place.
 */

import last from '../data/season-2025.json';
import { chalkOrder, lotteryBasis, toiletBowlRankOk } from './draftOrder.js';

interface Row { rank: number; team: string; gm: string; w: number; l: number; pf: number }

const LAST = last as {
  season: number;
  playoffTeams: number;
  standings: Row[];
  regularSeason: Row[];
  regularSeasonConfirmed: boolean;
  regularSeasonSource: string;
  champion: string;
  runnerUp: string;
  thirdPlace: string;
  fourthPlace: string;
  toiletBowlWinner: string;
  toiletBowlConfirmed: boolean;
  toiletBowlBasis: string;
};

export const SEASON = LAST.season;
export const PLAYOFF_TEAMS = LAST.playoffTeams;

/** The order of finish — playoffs on top, consolation bracket underneath. */
export const FINAL_ORDER: readonly string[] = LAST.standings.map((r) => r.gm);

/** Where everybody came in the regular season, off Yahoo's bracket seeds. */
export const REGULAR_ORDER: readonly string[] = LAST.regularSeason.map((r) => r.gm);

/** What the lottery odds are keyed to, and the only thing that should ever be. */
export const LOTTERY_BASIS: readonly string[] =
  lotteryBasis(FINAL_ORDER, REGULAR_ORDER, PLAYOFF_TEAMS);

export const CHAMPION = LAST.champion;
export const RUNNER_UP = LAST.runnerUp;
export const THIRD_PLACE = LAST.thirdPlace;
export const FOURTH_PLACE = LAST.fourthPlace;

export const TOILET_BOWL = {
  gm: LAST.toiletBowlWinner,
  confirmed: LAST.toiletBowlConfirmed,
  basis: LAST.toiletBowlBasis,
} as const;

export const REGULAR_SEASON_SOURCE = {
  confirmed: LAST.regularSeasonConfirmed,
  source: LAST.regularSeasonSource,
} as const;

/**
 * Both rows of a GM's season, for a screen that has to show which "place" it means.
 *
 * Returns 0 for a rank it cannot find rather than throwing: this feeds a chip on a card, and a
 * missing GM is a data problem to draw honestly, not a reason to blank the page.
 */
export function placesOf(gm: string): { final: number; regular: number } {
  const up = gm.toUpperCase();
  return {
    final: FINAL_ORDER.findIndex((g) => g.toUpperCase() === up) + 1,
    regular: REGULAR_ORDER.findIndex((g) => g.toUpperCase() === up) + 1,
  };
}

/** The place a GM's lottery weight is read off — his position in the basis. */
export function lotteryPlaceOf(gm: string): number {
  return LOTTERY_BASIS.findIndex((g) => g.toUpperCase() === gm.toUpperCase()) + 1;
}

/**
 * The consolation bracket is contested by the teams that missed the playoffs, so its winner
 * cannot hold a regular-season rank inside the bracket. Checked at module load because both
 * inputs feed a draw that happens once and cannot be re-run.
 */
export const TOILET_BOWL_RANK_OK =
  toiletBowlRankOk(REGULAR_ORDER, TOILET_BOWL.gm, PLAYOFF_TEAMS);

/* -- the board everybody is looking at before the lottery -------------------------------------- */

export interface ProjectedOrder {
  order: readonly string[];
  /** False once a real ten-team order is on the draft state. Then it is not a projection. */
  projected: boolean;
}

/**
 * THE ONE PROJECTED DRAFT ORDER, so three screens cannot show three of them.
 *
 * They did. `Home` projected from `LOTTERY_BASIS` — the regular-season table spliced under the
 * playoff places, which is the only thing the lottery odds are ever keyed to (Pass 06 §1) — and
 * `DraftBoard` projected from `standings`, the finish order WITH the playoff places on top. Those
 * two orders differ for everybody below the playoff line, which is most of the league and all of
 * the people who care where they pick. Home said one thing, the board said another, and the farm
 * rounds silently ran their serpentine against the board's.
 *
 * `finishOrder` on the draft state still wins where a commissioner has entered one: that is a
 * statement about this season rather than a projection from last.
 *
 * AND IT IS THE SAME FUNCTION BEFORE AND AFTER THE LOTTERY. A published ten-team order is
 * authoritative however it got there — drawn, or typed in on the settings screen — so the callers
 * do not branch. They read `projected` and label it.
 */
export function projectedOrder(
  draft: { order?: readonly string[]; finishOrder?: readonly string[];
    toiletBowlWinner?: string | null } | null | undefined,
  teams: number,
): ProjectedOrder {
  if (draft?.order?.length === teams) return { order: draft.order, projected: false };
  const basis = draft?.finishOrder?.length === teams ? draft.finishOrder : LOTTERY_BASIS;
  return {
    order: chalkOrder(basis, draft?.toiletBowlWinner ?? LAST.toiletBowlWinner),
    projected: true,
  };
}
